import type { DuplicateReviewComparison } from '@/hooks/usePatientMerge'
import { FIELD_LABELS, formatFieldValue, type MergeField } from './reviewFormat'

const FIELDS = Object.keys(FIELD_LABELS) as MergeField[]

const HEADER_CLASS =
  'py-2 pr-3 text-left text-[11px] font-semibold tracking-wider text-[#8A94A6] uppercase'

/**
 * The six demographics side by side. A field the records disagree on is highlighted; identical
 * fields still show each record's value, so neither column reads as missing data.
 */
export function DemographicsTable({
  records,
  differingFields,
}: {
  records: DuplicateReviewComparison['records']
  differingFields: MergeField[]
}) {
  const differing = new Set(differingFields)
  return (
    <table aria-label="Demographics" className="w-full table-fixed border-collapse text-[13px]">
      <thead>
        <tr className="border-b border-[#EAECEF]">
          <th className={`${HEADER_CLASS} w-[140px]`}>Field</th>
          {records.map((record, index) => (
            <th key={index} className={HEADER_CLASS}>
              {record.summary.name}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {FIELDS.map((field) => {
          const label = FIELD_LABELS[field]
          if (!differing.has(field)) {
            return (
              <tr key={field} className="border-b border-[#F0F1F3] last:border-b-0">
                <td className="py-2.5 pr-3 text-[#8A94A6]">{label}</td>
                {records.map((record, index) => (
                  <td key={index} className="py-2.5 pr-3 text-[#1F2733]">
                    {formatFieldValue(field, record.demographics)}
                  </td>
                ))}
              </tr>
            )
          }
          return (
            <tr key={field} className="border-b border-[#F0F1F3] last:border-b-0">
              <td className="py-2.5 pr-3 font-semibold text-[#1F2733]">
                {label}
                <span className="ml-2 rounded-full bg-[#FFF4E0] px-1.5 py-[1px] text-[10px] font-semibold text-[#B26A00]">
                  Differs
                </span>
              </td>
              {records.map((record, index) => (
                <td key={index} className="py-1.5 pr-3">
                  <div className="rounded-md bg-[#FFF8EC] px-2 py-1.5 text-[#1F2733]">
                    {formatFieldValue(field, record.demographics)}
                  </div>
                </td>
              ))}
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}
